<template>
  <entity-list-layout
    class="admin-organization-list"
    title="ORGANIZATION"
    :count="organizations.length"
    :query.sync="search"
    :search-hint="$t('label.search')"
  >
    <template #title-area-content>
      <managed-by-select
        v-if="showsManagedBy"
        v-model="managedBy"
        class="admin-organization-list__managed-by-select"
        :distributors="distributors"
      />
      <round-button class="my-2 mx-md-2" icon="add_circle" to="/admin/organizations/new">
        {{ $t('label.add_organization') }}
      </round-button>
    </template>
    <p-data-list :data="organizations" :pagination.sync="pagination">
      <data-field name="No" headerClass="column--no">
        <template v-slot="props">{{ props.index + 1 }}</template>
      </data-field>
      <data-field :name="$t('field.name')" value="name" sortable>
        <template v-slot="props">
          <router-link :to="{ name: 'admin-organization', params: { id: props.item.id }}">
            {{ props.item.name }}
          </router-link>
        </template>
      </data-field>
      <data-field :name="$t('field.address')" value="address" sortable>
        <template v-slot="props">{{ props.item.address }}</template>
      </data-field>
      <data-field :name="$t('field.phone_number')" value="phone_number" headerClass="column--phone-number" sortable>
        <template v-slot="props">{{ props.item.phone_number }}</template>
      </data-field>
      <data-field :name="$t('field.admin_user')" value="admin.name" headerClass="column--admin-user" sortable>
        <template v-slot="props">
          <router-link
            v-if="props.item.admin"
            :to="{ name: 'admin-account', params: { email: props.item.admin.email }}"
          >
            {{ props.item.admin.name }}
          </router-link>
          <div v-else class="text-center">---</div>
        </template>
      </data-field>
      <data-field
        v-if="showsManagedBy"
        :name="$t('field.managed_by')"
        value="managedBySortKey"
        headerClass="column--managed-by"
        sortable
      >
        <template v-slot="props">
          <span
            v-if="props.item.managedByName"
            class="admin-organization-list__managed-by"
            :class="{ 'text--disabled': props.item.managedByIsDirect }"
          >{{ props.item.managedByName }}</span>
          <span v-else data-testid="managed-by-empty">--</span>
        </template>
      </data-field>
    </p-data-list>
  </entity-list-layout>
</template>

<i18n lang="yaml">
ja:
  label:
    search: '文字列で検索'
    add_organization: '組織を追加'

  field:
    name: '組織名'
    address: '住所'
    phone_number: 'TEL'
    admin_user: '代表ユーザー'
    managed_by: '管理元'

  messages:
    failed_to_load: '組織一覧の読み込みに失敗しました'
    failed_to_load_distributors: '代理店の読み込みに失敗しました'

en:
  label:
    search: 'Search by text'
    add_organization: 'Add organization'

  field:
    name: 'Organization name'
    address: 'Address'
    phone_number: 'TEL'
    admin_user: 'Admin User'
    managed_by: 'Managed by'

  messages:
    failed_to_load: 'Failed to load organizations'
    failed_to_load_distributors: 'Failed to load distributors'
</i18n>

<script>
import EntityListLayout from '@/components/atoms/EntityListLayout';
import RoundButton from '@/components/atoms/RoundButton';
import ManagedBySelect from '@/components/molecules/ManagedBySelect';
import {
  describeManagedBy, directLabel, extractDistributors, matchesManagedBy,
} from '@/libs/managedBy';
import Features from '@/mixins/features';
import Notifications from '@/mixins/notifications';
import DataList, { Field } from '../DataList';

export default {
  name: 'admin-organization-list',
  components: {
    DataField: Field,
    EntityListLayout,
    ManagedBySelect,
    PDataList: DataList,
    RoundButton,
  },
  mixins: [Features, Notifications],
  created() {
    this.loadOrganizations();
  },
  computed: {
    // 管理元（選択欄・列・検索対象）は admin だけに見せる。代理店ログイン・代理ログイン中は admin が無く、
    // API も管理元を返さない。ログアウト直後は getCurrentUser が空になるため ?. で守る
    showsManagedBy() {
      return Boolean(this.$store.getters.getCurrentUser?.admin) && this.featureIsActive('distributor_account');
    },
    distributors() {
      return extractDistributors(this.$store.getters.getUsers.valuesArray());
    },
    // ログイン中のユーザー情報は取得のたびに差し替わるため、同一ユーザーかどうかはメールアドレスで判定する
    currentUserEmail() {
      return this.$store.getters.getCurrentUser?.email;
    },
    organizations() {
      const organizations = this.$store.getters.getOrganizations;
      const lowerSearchText = this.search.toLowerCase();
      // collections/Map の map は配列を返す
      return organizations
        .map(organization => ({ ...organization, ...this.managedByFields(organization) }))
        .filter(organization => (
          (!this.showsManagedBy || matchesManagedBy(organization.managing_distributor_id, this.managedBy))
          && this.organizationContains(lowerSearchText, organization)
        ));
    },
  },
  data() {
    return {
      pagination: {
        itemsPerPage: 20,
        sortBy: 'name',
      },
      search: '',
      managedBy: null,
    };
  },
  watch: {
    // 代理ログインの開始・解除でログイン中のユーザーが切り替わると一覧が消去されるため、取り直す
    currentUserEmail(email) {
      // ログアウト直後はユーザー情報が空になる。未ログインで取得しないようにする
      if (email != null) {
        this.loadOrganizations();
      }
    },
  },
  methods: {
    async loadOrganizations() {
      const requests = [this.$store.dispatch('requestOrganizations')];
      // 代理店名は users から引く。管理元を見せないときは取らない
      if (this.showsManagedBy) {
        requests.push(this.$store.dispatch('requestUsers'));
      }
      const [listResult, distributorsResult] = await Promise.allSettled(requests);
      // 画面遷移・代理ログインの切替・ログアウトで中断された取得（キャンセル）は通知しない
      const failed = result => result?.status === 'rejected' && !this.$http.isCancel(result.reason);
      // 通知は 1 件しか表示されないため、一覧そのものの失敗を優先する
      if (failed(listResult)) {
        this.notifyError('messages.failed_to_load');
      } else if (failed(distributorsResult)) {
        this.notifyError('messages.failed_to_load_distributors');
      }
    },
    managedByFields(item) {
      if (!this.showsManagedBy) return {};
      return describeManagedBy(item.managing_distributor_id, this.distributors, directLabel(this.$i18n.locale));
    },
    organizationContains(keywords, organization) {
      // eslint-disable-next-line arrow-body-style
      return this._.every(keywords.split(' '), (keyword) => {
        return this._.some([
          organization.name,
          organization.address,
          organization.phone_number,
          organization.admin?.name,
          organization.managedByName,
        ], (value) => {
          if (!value) return false;
          const lowerValue = value.toLowerCase();
          return this._.includes(lowerValue, keyword.toLowerCase());
        });
      });
    },
  },
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'

.admin-organization-list
  &__managed-by-select
    // 長い代理店名で幅が伸びて 2 段にならないよう固定し、選択中の名前は省略表示する。
    // スマホ表示では検索欄と同じく横幅いっぱいに広げる
    @media #{map-get($display-breakpoints, 'md-and-up')}
      width: 220px

  &__managed-by
    overflow-wrap: anywhere

  ::v-deep [role=columnheader]
    width: 50%

  ::v-deep .column
    &--no
      width: 48px

    &--phone-number
      min-width: 140px

    &--admin-user
      min-width: 120px
      text-align: center

    &--managed-by
      min-width: 140px
</style>
